'use client';

import type {
  FC,
  PropsWithChildren,
  ReactNode,
  HTMLAttributes,
  PropsWithoutRef,
} from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { cx } from '../../utils/cx';
import { Reveal } from '../Reveal';
import styles from './ContextualPopup.module.css';

export interface ContextualPopupProps<T = HTMLAttributes<HTMLDivElement>> {
  id?: string;
  behaviour?: 'manual' | 'hover' | 'click';
  handle?: ReactNode;
  handleComponent?: FC<PropsWithChildren<T>>;
  handleProps?: PropsWithoutRef<T>;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ContextualPopup: FC<PropsWithChildren<ContextualPopupProps>> = ({
  behaviour,
  handle,
  handleComponent: HandleComponent,
  handleProps,
  children,
  disabled: _disabled,
  open: manualOpen = false,
  onOpenChange,
  ...props
}) => {
  const portalElement =
    typeof document !== 'undefined' ? document.getElementById('portal') : null;
  const [open, setOpen] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [secondRender, setSecondRender] = useState(false);

  /**
   * Fix SSR issue by rendering modals only on the second render
   */
  useEffect(() => {
    setSecondRender(true);
  }, []);

  const refresh = useCallback(() => {
    const handle = handleRef.current;
    const popup = popupRef.current;

    if (!handle || !popup) return;

    const handleRect = handle.getClientRects()[0];
    const popupRect = popup.getClientRects()[0];

    const { scrollTop, clientWidth } =
      document.scrollingElement as typeof document.body;
    const viewport = window.innerHeight;

    const gap = 8;
    const margin = 16;

    const roomBelow = viewport - handleRect.bottom - gap;
    const roomAbove = handleRect.top - gap;
    const above = popupRect.height > roomBelow && roomAbove > roomBelow;

    const rawTop = above
      ? handleRect.top - popupRect.height - gap
      : handleRect.bottom + gap;

    const maxTop = Math.max(margin, viewport - popupRect.height - margin);
    const top = Math.max(margin, Math.min(rawTop, maxTop));

    const left = handleRect.left + handleRect.width / 2 - popupRect.width / 2;

    const minLeft = 16;
    const maxLeft = clientWidth - popupRect.width - 16;

    popup.style.top = `${top + scrollTop}px`;
    popup.style.left = `${
      left < minLeft ? minLeft : left > maxLeft ? maxLeft : left
    }px`;
  }, []);

  const setPopupOpen = (popupOpen: boolean) => {
    setOpen(popupOpen);
    onOpenChange?.(popupOpen);
  };

  const onHandleClick: HTMLAttributes<HTMLDivElement>['onClick'] = (event) => {
    event.stopPropagation();

    setPopupOpen(!open);
  };
  const onPopupClick: HTMLAttributes<HTMLDivElement>['onClick'] = (event) => {
    event.stopPropagation();
  };

  const onHandleMouseOver = () => {
    setPopupOpen(true);
  };
  const onHandleMouseOut = () => {
    setPopupOpen(false);
  };

  const onBackdropClick = () => {
    setPopupOpen(false);
  };

  const isOpen = behaviour === 'manual' ? manualOpen : open;

  useLayoutEffect(() => {
    if (!isOpen || !secondRender) return;
    refresh();
    addEventListener('resize', refresh);
    return () => removeEventListener('resize', refresh);
  }, [isOpen, secondRender, refresh]);

  return (
    <>
      <span
        className={styles.handle}
        ref={handleRef}
        onClick={behaviour === 'click' ? onHandleClick : undefined}
        onMouseOver={behaviour === 'hover' ? onHandleMouseOver : undefined}
        onMouseOut={behaviour === 'hover' ? onHandleMouseOut : undefined}
        {...handleProps}
      >
        {HandleComponent ? <HandleComponent /> : handle}
      </span>

      {portalElement &&
        secondRender &&
        ReactDOM.createPortal(
          <>
            {open && (
              <div className={styles.popupBackdrop} onClick={onBackdropClick} />
            )}

            <div
              className={styles.popupRoot}
              data-open={isOpen}
              ref={popupRef}
              onClick={onPopupClick}
              {...props}
            >
              <Reveal
                className={cx(
                  styles.popupBackground,
                  open && styles.popupBackgroundOpen
                )}
                size={480}
                spread={0}
              />
              <Reveal className={styles.popupBorder} />

              {children}
            </div>
          </>,
          portalElement
        )}
    </>
  );
};
